import {useState, useEffect} from 'react';
import getAPI from '../functions/API/getAPI.js'
import getTaskFlowDetails from '../functions/API/getTaskFlowDetails.js'
import postSendOnboardingInvite from '../functions/API/postSendOnboardingInvite.js'
import { toast } from '../lib/toast.js';

export default function SendOnboardingInviteForm({setVisible, refresh, refreshValue}) {
    const [taskFlows, setTaskFlows] = useState([]);
    const [paymentSteps, setPaymentSteps] = useState([]);
    const [paymentStepAmounts, setPaymentStepAmounts] = useState([]);
    const [signingSteps, setSigningSteps] = useState([]);
    const [signingStepOverrides, setSigningStepOverrides] = useState([]);
    const [signingOverrideEnabled, setSigningOverrideEnabled] = useState({});
    const [agreementNameOverrides, setAgreementNameOverrides] = useState({});
    const [allFlowSteps, setAllFlowSteps] = useState([]);
    const [loadingSteps, setLoadingSteps] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        first_name: "",
        last_name: "",
        email: "",
        task_flow_id: "",
    });

    useEffect(() => {
        async function fetchData() {
            const flowsRes = await getAPI('taskflows');
            if (flowsRes.response.ok && flowsRes.data?.task_flows) {
                const onboardingFlows = flowsRes.data.task_flows.filter(
                    tf => tf.is_onboarding && !tf.is_archived
                );
                setTaskFlows(onboardingFlows);
            }
        }
        fetchData();
    }, []);

    // Fetch flow details when task flow changes to detect payment steps
    useEffect(() => {
        if (!formData.task_flow_id) {
            setPaymentSteps([]);
            setPaymentStepAmounts([]);
            setSigningSteps([]);
            setSigningStepOverrides([]);
            setSigningOverrideEnabled({});
            setAllFlowSteps([]);
            return;
        }

        async function fetchFlowDetails() {
            setLoadingSteps(true);
            try {
                const res = await getTaskFlowDetails(parseInt(formData.task_flow_id));
                if (res.response.ok && res.data?.task_flow?.steps) {
                    const steps = res.data.task_flow.steps;
                    setAllFlowSteps(steps);

                    const pSteps = steps.filter(s => s.step_type === 'payment');
                    setPaymentSteps(pSteps);
                    setPaymentStepAmounts(pSteps.map(s => ({
                        task_flow_step_id: s.id,
                        amount: '',
                    })));

                    const sSteps = steps.filter(s => s.step_type === 'document_signing');
                    setSigningSteps(sSteps);
                    setSigningStepOverrides(sSteps.map(s => ({
                        task_flow_step_id: s.id,
                        field_overrides: (s.step_config.field_mappings || [])
                            .filter(m => m.source_type !== 'customer_enters')
                            .map(m => ({
                                adobe_field_name: m.adobe_field_name,
                                value: m.source_type === 'static_value' ? (m.static_value || '') : '',
                                default_source: m.source_type,
                            })),
                    })));
                    setSigningOverrideEnabled({});
                } else {
                    setPaymentSteps([]);
                    setPaymentStepAmounts([]);
                    setSigningSteps([]);
                    setSigningStepOverrides([]);
                    setSigningOverrideEnabled({});
                    setAgreementNameOverrides({});
                    setAllFlowSteps([]);
                }
            } catch {
                setPaymentSteps([]);
                setPaymentStepAmounts([]);
                setSigningSteps([]);
                setSigningStepOverrides([]);
                setSigningOverrideEnabled({});
                setAgreementNameOverrides({});
            }
            setLoadingSteps(false);
        }
        fetchFlowDetails();
    }, [formData.task_flow_id]);

    async function handleSubmit() {
        if (!formData.first_name || !formData.last_name || !formData.email || !formData.task_flow_id) {
            toast.error('First name, last name, email, and onboarding flow are required.');
            return;
        }

        // Validate payment step amounts
        if (paymentSteps.length > 0) {
            for (const ps of paymentStepAmounts) {
                const amount = parseFloat(ps.amount);
                if (!amount || amount < 0.01) {
                    const step = paymentSteps.find(s => s.id === ps.task_flow_step_id);
                    toast.error(`Payment amount for "${step?.name || 'payment step'}" must be at least £0.01.`);
                    return;
                }
            }
        }

        setSubmitting(true);

        const body = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            task_flow_id: parseInt(formData.task_flow_id),
        };
        if (paymentSteps.length > 0) {
            body.payment_step_amounts = paymentStepAmounts.map(ps => ({
                task_flow_step_id: ps.task_flow_step_id,
                amount: parseFloat(ps.amount),
            }));
        }
        if (signingSteps.length > 0) {
            const overridesWithValues = signingStepOverrides
                .filter(so => signingOverrideEnabled[so.task_flow_step_id])
                .map(so => ({
                    task_flow_step_id: so.task_flow_step_id,
                    field_overrides: so.field_overrides
                        .filter(fo => fo.value !== '')
                        .map(fo => ({ adobe_field_name: fo.adobe_field_name, value: fo.value })),
                }))
                .filter(so => so.field_overrides.length > 0);

            if (overridesWithValues.length > 0) {
                body.signing_step_overrides = overridesWithValues;
            }

            const nameOverrides = Object.entries(agreementNameOverrides)
                .filter(([, name]) => name.trim() !== '')
                .map(([stepId, name]) => ({ task_flow_step_id: parseInt(stepId), agreement_name: name.trim() }));
            if (nameOverrides.length > 0) {
                body.agreement_name_overrides = nameOverrides;
            }
        }

        const response = await postSendOnboardingInvite(body);
        if (response.response.ok) {
            refresh(refreshValue + 1);
            toast.success('Onboarding invite sent successfully.');
            setVisible(false);
        } else {
            const errorMsg = response.data?.message || 'Failed to send invite. Try again later.';
            if (typeof errorMsg === 'object') {
                const messages = Object.values(errorMsg).flat();
                messages.forEach(m => toast.error(m));
            } else {
                toast.error(errorMsg);
            }
        }
        setSubmitting(false);
    }

    function handleChange(event) {
        let name = event.target.name;
        let value = event.target.value;
        setFormData({...formData, [name]: value});
    }

    function updatePaymentAmount(stepId, value) {
        setPaymentStepAmounts(prev =>
            prev.map(ps =>
                ps.task_flow_step_id === stepId ? { ...ps, amount: value } : ps
            )
        );
    }

    function formatPaymentAmount(stepId) {
        setPaymentStepAmounts(prev =>
            prev.map(ps => {
                if (ps.task_flow_step_id !== stepId) return ps;
                const num = parseFloat(ps.amount);
                if (isNaN(num)) return ps;
                return { ...ps, amount: num.toFixed(2) };
            })
        );
    }

    function updateSigningFieldOverride(stepId, fieldName, value) {
        setSigningStepOverrides(prev =>
            prev.map(so => {
                if (so.task_flow_step_id !== stepId) return so;
                return {
                    ...so,
                    field_overrides: so.field_overrides.map(fo =>
                        fo.adobe_field_name === fieldName ? { ...fo, value } : fo
                    ),
                };
            })
        );
    }

    function getSourceLabel(mapping, steps) {
        if (mapping.source_type === 'static_value') return 'Static: ' + (mapping.static_value || '—');
        if (mapping.source_type === 'task_data') {
            const sourceStep = steps?.find(s => s.id === mapping.source_step_id);
            const sourceField = sourceStep?.fields?.find(f => f.id === mapping.source_field_id);
            return 'From: ' + (sourceStep?.name || '?') + ' → ' + (sourceField?.name || '?');
        }
        return mapping.source_type;
    }

    return (
        <form>
            <div className="space-y-12">
                <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
                        Send onboarding invite
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-400">Send a magic link to invite a prospective tenant to create their account. The link will expire after 7 days. Automatic reminders will be sent if the invite is not used.</p>
                </div>

                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-3 md:col-span-1">
                    <div className="sm:col-span-3">
                        <label htmlFor="first_name" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            First Name
                        </label>
                        <div className="mt-2">
                            <input
                                required
                                type="text"
                                onChange={handleChange}
                                name="first_name"
                                id="first_name"
                                value={formData.first_name}
                                className="pl-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                                placeholder="First name"
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="last_name" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Last Name
                        </label>
                        <div className="mt-2">
                            <input
                                required
                                type="text"
                                onChange={handleChange}
                                name="last_name"
                                id="last_name"
                                value={formData.last_name}
                                className="pl-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                                placeholder="Last name"
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Email Address
                        </label>
                        <div className="mt-2">
                            <input
                                required
                                type="email"
                                onChange={handleChange}
                                name="email"
                                id="email"
                                value={formData.email}
                                className="pl-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                                placeholder="email@example.com"
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-3">
                        <label htmlFor="task_flow_id" className="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                            Onboarding Flow
                        </label>
                        <div className="mt-2">
                            <select
                                onChange={handleChange}
                                name="task_flow_id"
                                id="task_flow_id"
                                value={formData.task_flow_id}
                                className="pl-2 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                            >
                                <option value="">Select an onboarding flow...</option>
                                {taskFlows.map(tf => (
                                    <option key={tf.id} value={tf.id}>{tf.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Payment step amounts */}
                    {loadingSteps && formData.task_flow_id && (
                        <div className="sm:col-span-3">
                            <p className="text-sm text-gray-500 dark:text-gray-400">Loading flow details...</p>
                        </div>
                    )}
                    {paymentSteps.length > 0 && (
                        <div className="sm:col-span-3 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h3 className="text-sm font-semibold text-green-900 mb-3">Payment Amounts</h3>
                            <p className="text-xs text-green-700 mb-4">Set the payment amount for each payment step in this flow.</p>
                            <div className="space-y-3">
                                {paymentSteps.map(ps => {
                                    const amountEntry = paymentStepAmounts.find(a => a.task_flow_step_id === ps.id);
                                    return (
                                        <div key={ps.id}>
                                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                                                {ps.name}
                                            </label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">{'\u00A3'}</span>
                                                <input
                                                    type="number"
                                                    min="0.01"
                                                    step="0.01"
                                                    value={amountEntry?.amount ?? ''}
                                                    onChange={(e) => updatePaymentAmount(ps.id, e.target.value)}
                                                    onBlur={() => formatPaymentAmount(ps.id)}
                                                    placeholder="0.00"
                                                    className="pl-7 block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Document signing overrides */}
                    {signingSteps.length > 0 && signingSteps.map(ss => {
                        const stepOverride = signingStepOverrides.find(so => so.task_flow_step_id === ss.id);
                        const hasFieldOverrides = stepOverride && stepOverride.field_overrides.length > 0;
                        const enabled = !!signingOverrideEnabled[ss.id];
                        return (
                            <div key={ss.id} className="sm:col-span-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                                <h3 className="text-sm font-semibold text-amber-900 mb-3">{ss.name}</h3>

                                {/* Agreement name override */}
                                <div className="mb-3">
                                    <label className="block text-sm font-medium text-gray-900 dark:text-white mb-0.5">
                                        Agreement Name
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                        Default: {ss.step_config?.agreement_name
                                            ? ss.step_config.agreement_name.replace(/\{\{(.+?)\}\}/g, (_, name) => `[${name}]`)
                                            : (ss.step_config?.template_name || 'Template name')}
                                    </p>
                                    <input
                                        type="text"
                                        value={agreementNameOverrides[ss.id] || ''}
                                        onChange={(e) => setAgreementNameOverrides(prev => ({ ...prev, [ss.id]: e.target.value }))}
                                        placeholder="Leave blank for default"
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm"
                                    />
                                </div>

                                {/* Field overrides toggle */}
                                {hasFieldOverrides && (
                                <>
                                <div className="flex items-center justify-between border-t border-amber-200 pt-3">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <span className="text-xs text-amber-700">Override field values</span>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={enabled}
                                            onClick={() => setSigningOverrideEnabled(prev => ({ ...prev, [ss.id]: !prev[ss.id] }))}
                                            className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${enabled ? 'bg-amber-600' : 'bg-gray-200 dark:bg-zinc-600'}`}
                                        >
                                            <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                                        </button>
                                    </label>
                                </div>
                                {!enabled && (
                                    <p className="text-xs text-amber-700 mt-1">
                                        Default field mappings will be used.
                                    </p>
                                )}
                                {enabled && (
                                    <>
                                        <p className="text-xs text-amber-700 mt-1 mb-4">
                                            Override the pre-filled values for this document. Leave blank to use the default mapping.
                                        </p>
                                        <div className="space-y-3">
                                            {stepOverride.field_overrides.map(fo => {
                                                const originalMapping = ss.step_config?.field_mappings?.find(m => m.adobe_field_name === fo.adobe_field_name);
                                                return (
                                                    <div key={fo.adobe_field_name}>
                                                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-0.5">
                                                            {fo.adobe_field_name}
                                                        </label>
                                                        {originalMapping && (
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                                                Default: {getSourceLabel(originalMapping, allFlowSteps)}
                                                            </p>
                                                        )}
                                                        <input
                                                            type="text"
                                                            value={fo.value}
                                                            onChange={(e) => updateSigningFieldOverride(ss.id, fo.adobe_field_name, e.target.value)}
                                                            placeholder="Leave blank for default"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm"
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                                </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mt-6 flex items-center justify-start gap-x-6">
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:opacity-50"
                >
                    {submitting ? 'Sending...' : 'Send Invite'}
                </button>
                <button type="button" onClick={() => setVisible(false)} className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                    Cancel
                </button>
            </div>
        </form>
    );
}
